import { useState, useCallback, useEffect, useMemo } from 'react'
import { Modal, ModalDialog, ModalClose, Typography, Divider, Stack, FormControl, FormLabel, Button, Alert, IconButton, Select, Option } from '@mui/joy'
import CloseIcon from '@mui/icons-material/Close'

import { importGroupsFromAccount } from '../../../api/telegramApi'
import { type TelegramAccount } from '../types'

interface ImportGroupsModalProps {
    open: boolean
    accounts: TelegramAccount[]
    onClose: () => void
    onImport: () => void
}

export function ImportGroupsModal({ open, accounts, onClose, onImport }: ImportGroupsModalProps): JSX.Element {
    const [selectedAccount, setSelectedAccount] = useState<string | null>(null)
    const [importing, setImporting] = useState(false)
    const [importResult, setImportResult] = useState<{ imported: number; groups: Array<{ peerId: string; originalName: string | null }> } | null>(null)
    const [modalError, setModalError] = useState<string | null>(null)

    const activeAccounts = useMemo(() => accounts.filter((a) => a.status === 'active'), [accounts])

    const resetState = useCallback(() => {
        setSelectedAccount(null)
        setImporting(false)
        setImportResult(null)
        setModalError(null)
    }, [])

    useEffect(() => {
        if (!open) {
            resetState()
        }
    }, [open, resetState])

    const handleImport = useCallback(async () => {
        if (!selectedAccount) return
        setImporting(true)
        setImportResult(null)
        setModalError(null)
        try {
            const result = await importGroupsFromAccount(selectedAccount)
            setImportResult(result)
            onImport()
        } catch (e) {
            setModalError((e as Error)?.message || 'Failed to import groups')
        } finally {
            setImporting(false)
        }
    }, [selectedAccount, onImport])

    const handleModalClose = useCallback(() => {
        resetState()
        onClose()
    }, [onClose, resetState])

    return (
        <Modal open={open} onClose={handleModalClose}>
            <ModalDialog variant="outlined" sx={{ borderRadius: 'lg', minWidth: 500 }}>
                <ModalClose />
                <Typography level="h4" component="h2">
                    Import Groups
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                    Import groups from a connected Telegram account
                </Typography>
                <Divider sx={{ my: 2 }} />

                {modalError && (
                    <Alert
                        color="danger"
                        variant="soft"
                        sx={{ mb: 2 }}
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="danger" onClick={() => setModalError(null)}>
                                <CloseIcon />
                            </IconButton>
                        }
                    >
                        {modalError}
                    </Alert>
                )}

                {importResult && (
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        Imported {importResult.imported} group{importResult.imported !== 1 ? 's' : ''}
                    </Alert>
                )}

                <Stack spacing={2}>
                    {activeAccounts.length === 0 ? (
                        <Alert color="warning" variant="soft">
                            No active accounts available. Please sign in to a Telegram account first.
                        </Alert>
                    ) : (
                        <>
                            <FormControl>
                                <FormLabel>Select Account</FormLabel>
                                <Select
                                    placeholder="Choose an active account"
                                    value={selectedAccount}
                                    onChange={(_, value) => setSelectedAccount(value)}
                                    disabled={importing}
                                >
                                    {activeAccounts.map((account) => (
                                        <Option key={account.phone} value={account.phone}>
                                            {account.phone}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button variant="solid" color="primary" onClick={handleImport} disabled={!selectedAccount} loading={importing} fullWidth>
                                Import Groups
                            </Button>
                        </>
                    )}

                    <Button variant="plain" color="neutral" onClick={handleModalClose}>
                        Close
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    )
}
